import {useCallback, useMemo, useRef} from 'react';

import {type PopoverAnimation, popoverFactory} from '../layout/Popover/Popover';

import type {PopoverState} from '../layout/Popover/usePopoverAnimation';

/**
 * Custom hook for managing modal state and functionality.
 *
 * This hook provides a way to manage modal state and functionality in a component.
 * It returns an object containing the `openModal`, `closeModal`, and `Modal` properties.
 *
 * @param animation        The animation to use for the modal.
 * @param popoverAlignment The alignment of the popover content, which can be 'center', 'left', or 'right'.
 * @returns An object with modal state and functionality.
 *
 * @example
 * ```tsx
 * const { openModal, closeModal, Modal } = useModal();
 * ```
 */
export const usePopover = (animation: PopoverAnimation, popoverAlignment: 'left' | 'right') => {
    const state = useRef<PopoverState>({
        /**
         * Sets the open state of the modal.
         */
        setOpen() {}
    });
    const Popover = useMemo(() => popoverFactory(state, animation), [animation]);

    /**
     * Opens the modal.
     *
     * This function sets the state of the modal to `true`, opening the modal.
     */
    const openPopover = useCallback(() => {
        state.current.setOpen(true);
    }, [state]);

    /**
     * Closes the modal.
     *
     * This function sets the state of the modal to `false`, closing the modal.
     *
     * @param onClose An optional callback function to be called when the modal is closed.
     */
    const closePopover = useCallback(() => {
        state.current.setOpen(false);
    }, [state]);

    const alignmentToTrianglePositionMap = new Map([
        ['left', 100],
        ['right', 0]
    ]);

    const alignmentToTriangleOffsetMap = new Map<string, `- ${number}px` | `+ ${number}px`>([
        ['left', '- 9px'],
        ['right', '+ 9px']
    ]);

    return {
        closePopover,
        openPopover,
        Popover,
        triangleOffset: alignmentToTriangleOffsetMap.get(popoverAlignment),
        trianglePosition: alignmentToTrianglePositionMap.get(popoverAlignment)
    };
};